import React, { useState, useEffect } from 'react';
import { FileText, Download, Edit3, CheckCircle, Loader2, Sparkles, Eye, Save, BarChart3, TrendingUp, Activity, ArrowLeft, Home } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';

// Helper to format currency
const formatCurrency = (val: number) => {
  if (!val && val !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
};

// Claim Breakdown Chart Component
const ClaimBreakdownChart = ({ step1, step2, total }: { step1: number; step2: number; total: number }) => {
  if (!total || total <= 0) return null;
  
  const step1Pct = (step1 / total) * 100;
  const step2Pct = (step2 / total) * 100;
  
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
      <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-indigo-600" />
        Claim Amount Breakdown
      </h4>
      
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Step 1: Variable Profit Loss</span>
            <span className="font-semibold">{formatCurrency(step1)} ({step1Pct.toFixed(0)}%)</span>
          </div>
          <div className="h-6 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full flex items-center justify-end pr-2"
              style={{ width: `${step1Pct}%` }}
            >
              {step1Pct > 15 && <span className="text-white text-xs font-bold">{formatCurrency(step1)}</span>}
            </div>
          </div>
        </div>
        
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Step 2: Incremental Loss</span>
            <span className="font-semibold">{formatCurrency(step2)} ({step2Pct.toFixed(0)}%)</span>
          </div>
          <div className="h-6 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-end pr-2"
              style={{ width: `${step2Pct}%` }}
            >
              {step2Pct > 15 && <span className="text-white text-xs font-bold">{formatCurrency(step2)}</span>}
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-300 text-center">
        <span className="text-lg font-bold text-green-700">Total Claim: {formatCurrency(total)}</span>
      </div>
    </div>
  );
};

// Sensitivity Analysis Chart Component
const SensitivityChart = ({ data }: { data: any }) => {
  if (!data || !data.results) return null;
  
  const results = data.results;
  const base = results.base_claim || 0;
  const range = results.range || {};
  const minVal = range.min || 0;
  const maxVal = range.max || 0;
  const spread = range.spread || 0;
  const param = (data.parameter || 'parameter').replace('_', ' ');
  const spreadPct = base > 0 ? (spread / base) * 100 : 0;
  
  const getSensitivityLabel = () => {
    if (spreadPct < 15) return { label: 'Low Sensitivity', color: 'text-green-600', bg: 'bg-green-100' };
    if (spreadPct < 30) return { label: 'Moderate Sensitivity', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { label: 'High Sensitivity', color: 'text-red-600', bg: 'bg-red-100' };
  };
  
  const sensitivity = getSensitivityLabel();
  
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
      <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-blue-800">
        <TrendingUp className="w-5 h-5" />
        Sensitivity Analysis: {param.charAt(0).toUpperCase() + param.slice(1)}
      </h4>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-xs text-gray-500">Min (-20%)</div>
          <div className="text-lg font-bold text-blue-600">{formatCurrency(minVal)}</div>
        </div>
        <div className="bg-green-600 rounded-lg p-4 text-center">
          <div className="text-xs text-green-100">Base Claim</div>
          <div className="text-lg font-bold text-white">{formatCurrency(base)}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-xs text-gray-500">Max (+20%)</div>
          <div className="text-lg font-bold text-blue-600">{formatCurrency(maxVal)}</div>
        </div>
      </div>
      
      <div className="h-4 bg-gradient-to-r from-blue-200 via-green-300 to-blue-200 rounded-full mb-4 relative">
        <div className="absolute left-1/2 top-0 w-1 h-6 bg-green-600 rounded transform -translate-x-1/2 -translate-y-1"></div>
      </div>
      
      <div className="text-center">
        <span className="text-sm text-gray-600">Spread: {formatCurrency(spread)} ({spreadPct.toFixed(1)}% of base) — </span>
        <span className={`font-bold ${sensitivity.color} ${sensitivity.bg} px-2 py-1 rounded`}>{sensitivity.label}</span>
      </div>
    </div>
  );
};

// Monte Carlo Chart Component
const MonteCarloChart = ({ data }: { data: any }) => {
  if (!data || !data.results) return null;
  
  const outerResults = data.results || {};
  const results = outerResults.results || outerResults;
  const iterations = data.iterations || outerResults.iterations || 1000;
  const mean = results.mean || 0;
  const median = results.median || 0;
  const stdDev = results.std_dev || 0;
  const ciLow = results.ci_95_low || 0;
  const ciHigh = results.ci_95_high || 0;
  
  if (ciHigh <= 0) return null;
  
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
      <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-blue-800">
        <Activity className="w-5 h-5" />
        Monte Carlo Simulation ({iterations.toLocaleString()} iterations)
      </h4>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-xs text-gray-500">Mean</div>
          <div className="text-lg font-bold text-blue-600">{formatCurrency(mean)}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-xs text-gray-500">Median</div>
          <div className="text-lg font-bold text-blue-600">{formatCurrency(median)}</div>
        </div>
        <div className="bg-white rounded-lg p-4 text-center">
          <div className="text-xs text-gray-500">Std Dev</div>
          <div className="text-lg font-bold text-blue-600">{formatCurrency(stdDev)}</div>
        </div>
      </div>
      
      <div className="bg-blue-100 rounded-lg p-4">
        <div className="text-center text-sm font-bold text-blue-800 mb-3">95% Confidence Interval</div>
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div className="text-xs text-gray-500">Low Bound</div>
            <div className="text-lg font-bold text-blue-700">{formatCurrency(ciLow)}</div>
          </div>
          <div className="flex-1 mx-4 h-6 bg-gradient-to-r from-blue-300 via-blue-500 to-blue-300 rounded-full relative">
            <div className="absolute left-1/2 top-0 w-1 h-8 bg-yellow-500 rounded transform -translate-x-1/2 -translate-y-1"></div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">High Bound</div>
            <div className="text-lg font-bold text-blue-700">{formatCurrency(ciHigh)}</div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 text-center text-sm bg-yellow-50 p-3 rounded-lg">
        <strong>Interpretation:</strong> 95% probability the true claim value lies within {formatCurrency(ciLow)} to {formatCurrency(ciHigh)}
      </div>
    </div>
  );
};

/**
 * AI Report Narrator - Frontend Editor
 * Premium Feature: $200/report
 * 
 * Allows users to:
 * - Generate AI-powered 30-40 page comprehensive legal narrative
 * - View existing narratives without regeneration
 * - Edit sections as needed
 * - Preview in real-time
 * - Save to database
 * - Export to PDF anytime
 */

interface NarrativeEditorProps {
  claimId: string;
  claimData: any;
  calculationResults: any;
  eventType: string;
  existingNarrative?: any;  // Load from report if exists
  isAccountantView?: boolean;  // Whether viewing from accountant portal
}

export default function NarrativeEditor({
  claimId,
  claimData,
  calculationResults,
  eventType,
  existingNarrative,
  isAccountantView = false
}: NarrativeEditorProps) {
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [narrative, setNarrative] = useState(existingNarrative || null);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionContent, setSectionContent] = useState('');
  const [refining, setRefining] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editorMessage, setEditorMessage] = useState<string | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Determine back navigation path
  const getBackPath = () => {
    if (isAccountantView || location.pathname.startsWith('/accountant/')) {
      return `/accountant/report/${claimId}`;
    }
    return `/report/${claimId}`;
  };

  const getDashboardPath = () => {
    if (isAccountantView || location.pathname.startsWith('/accountant/')) {
      return '/accountant/dashboard';
    }
    return '/dashboard';
  };

  // Update narrative when existingNarrative prop changes (e.g., after fetch)
  useEffect(() => {
    if (existingNarrative && !narrative) {
      setNarrative(existingNarrative);
      console.log('Loaded existing narrative from database');
    }
  }, [existingNarrative]);

  // Section definitions
  const sections = [
    { id: 'executive_summary', name: 'Executive Summary', pages: 1 },
    { id: 'causation_analysis', name: 'Causation Analysis', pages: 2 },
    { id: 'methodology', name: 'Methodology Overview', pages: 2 },
    { id: 'benchmark_analysis', name: 'Benchmark Period Analysis', pages: 2 },
    { id: 'detailed_calculations', name: 'Detailed Calculations', pages: 3 },
    { id: 'assumptions', name: 'Assumptions & Limitations', pages: 2 },
    { id: 'attestation', name: 'Professional Attestation', pages: 1 },
    { id: 'conclusion', name: 'Conclusion & Recommendation', pages: 1 }
  ];

  // Generate narrative
  const handleGenerateNarrative = async () => {
    setGenerating(true);
    try {
      const token = await getToken();
      const result = await api.post('/api/v1/narrator/generate-narrative', {
        claim_id: claimId,
        claim_data: claimData,
        calculation_results: calculationResults,
        event_type: eventType
      }, { headers: { Authorization: `Bearer ${token}` } });

      setNarrative(result.narrative);
      
      setEditorMessage(`Generated ${result.narrative.estimated_pages}-page report (${result.narrative.word_count} words). Review each section below.`);
    } catch (error: any) {
      console.error('Generation error:', error);
      setEditorError(error.message || 'Failed to generate narrative. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Edit section
  const handleEditSection = (sectionId: string) => {
    // If sections are empty (MVP parsing), show full narrative for editing
    const sectionText = narrative?.narrative_sections?.[sectionId];
    const fullText = narrative?.narrative_sections?.full_narrative || narrative?.full_narrative || '';
    
    const content = sectionText && sectionText.trim().length > 0 ? sectionText : fullText;
    setSectionContent(content);
    setEditingSection(sectionId);
  };

  // Refine section with AI
  const handleRefineSection = async (feedback: string) => {
    if (!feedback.trim()) {
      setEditorError('Please provide feedback for AI refinement');
      return;
    }

    setRefining(true);
    try {
      const token = await getToken();
      const result = await api.post('/api/v1/narrator/refine-section', {
        claim_id: claimId,
        section_name: editingSection,
        current_text: sectionContent,
        user_feedback: feedback
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSectionContent(result.refined_text);
      
      // Update in narrative
      setNarrative(prev => ({
        ...prev,
        narrative_sections: {
          ...prev.narrative_sections,
          [editingSection]: result.refined_text
        }
      }));

      setEditorMessage('Section refined by AI');
    } catch (error) {
      console.error('Refinement error:', error);
      setEditorError('Failed to refine section');
    } finally {
      setRefining(false);
    }
  };

  // Save section locally and mark as having unsaved changes
  const handleSaveSection = () => {
    setNarrative(prev => ({
      ...prev,
      narrative_sections: {
        ...prev.narrative_sections,
        [editingSection]: sectionContent
      }
    }));
    setEditingSection(null);
    setHasUnsavedChanges(true);
    setEditorMessage('Section saved locally. Click "Save All Changes" to persist to database.');
  };

  // Save all changes to database
  const handleSaveToDatabase = async () => {
    if (!narrative) return;
    
    setSaving(true);
    try {
      const token = await getToken();
      await api.post('/api/v1/narrator/save-narrative', {
        claim_id: claimId,
        narrative_sections: narrative.narrative_sections,
        word_count: narrative.word_count,
        estimated_pages: narrative.estimated_pages
      }, { headers: { Authorization: `Bearer ${token}` } });

      setHasUnsavedChanges(false);
      setEditorMessage('All changes saved to database successfully!');
    } catch (error: any) {
      console.error('Save error:', error);
      setEditorError(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Export to PDF
  const handleExportPDF = async () => {
    // First save any pending changes
    if (hasUnsavedChanges) {
      await handleSaveToDatabase();
    }
    
    setGenerating(true);
    try {
      const token = await getToken();
      const result = await api.post('/api/v1/narrator/generate-narrative-pdf', {
        claim_id: claimId,
        claim_data: claimData,
        calculation_results: {
          ...calculationResults,
          narrative_sections: narrative.narrative_sections
        },
        event_type: eventType
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Download the PDF
      window.open(result.pdf_url, '_blank');
      
      setEditorMessage('14-page PDF generated! Opening download...');
      
      // Optionally navigate back to report detail
      // navigate(`/report/${claimId}`);
      
    } catch (error) {
      console.error('PDF export error:', error);
      setEditorError('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Navigation Bar */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(getBackPath())}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Report
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => navigate(getDashboardPath())}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
            >
              <Home className="w-5 h-5" />
              Dashboard
            </button>
          </div>
          <div className="text-sm text-gray-500">
            Claim: {claimData?.claimant_name || claimData?.client_name || 'Unknown'}
          </div>
        </div>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8 text-blue-600" />
                <h1 className="text-3xl font-bold">AI Report Narrator</h1>
                <span className="bg-gradient-to-r from-blue-500 to-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  Premium $150
                </span>
              </div>
              <p className="text-gray-600">Generate court-ready 14-page legal narrative in seconds</p>
            </div>
            <div className="flex gap-3">
              {!narrative ? (
                <button
                  onClick={handleGenerateNarrative}
                  disabled={generating}
                  className="bg-gradient-to-r from-blue-600 to-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate AI Report
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to regenerate the narrative? This will replace the existing content.')) {
                      handleGenerateNarrative();
                    }
                  }}
                  disabled={generating}
                  className="bg-orange-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Regenerate Report
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Generation in progress */}
        {generating && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">AI is writing your report...</h2>
            <p className="text-gray-600 mb-4">This takes 30-60 seconds</p>
            <div className="max-w-md mx-auto bg-gray-100 rounded p-4">
              <p className="text-sm text-gray-700">
                Claude 3.5 Sonnet is analyzing your claim data, BI calculations, and generating a 
                professional legal narrative with proper citations, methodology explanations, and attestation language.
              </p>
            </div>
          </div>
        )}

        {/* Narrative generated - Section editor */}
        {narrative && !editingSection && (
          <div className="space-y-4">
            {/* Unsaved changes warning */}
            {hasUnsavedChanges && (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-bold text-yellow-800">You have unsaved changes</p>
                    <p className="text-sm text-yellow-700">Click "Save All Changes" to persist your edits to the database</p>
                  </div>
                </div>
                <button
                  onClick={handleSaveToDatabase}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white rounded-lg font-bold hover:bg-yellow-600 transition disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save All Changes
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Warning for empty/broken narratives */}
            {narrative && (!narrative.word_count || narrative.word_count === 0) && (
              <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-bold text-red-800">Empty or Failed Narrative</p>
                    <p className="text-sm text-red-700">This narrative appears to be empty or failed to generate. Click "Regenerate Report" above to create a new one.</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold">Your AI-Generated Report</h2>
                  <p className="text-gray-600">
                    {narrative.estimated_pages || 0} pages • {narrative.word_count || 0} words • Review & edit each section
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                  >
                    <Eye className="w-4 h-4" />
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </button>
                  {hasUnsavedChanges && (
                    <button
                      onClick={handleSaveToDatabase}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  )}
                  <button
                    onClick={handleExportPDF}
                    disabled={generating}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    Export PDF
                  </button>
                </div>
              </div>

              {/* Section cards */}
              <div className="grid grid-cols-1 gap-4">
                {/* Single comprehensive report card */}
                <div
                  className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-400 transition cursor-pointer"
                  onClick={() => handleEditSection('full_narrative')}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-6 h-6 text-blue-600" />
                      <h3 className="font-bold text-lg">Court-Ready Expert Report</h3>
                    </div>
                    <span className="text-xs text-gray-500">
                      {narrative?.estimated_pages || 5} pages • {narrative?.word_count || 0} words
                    </span>
                  </div>
                  <div className="mb-3">
                    <p className="text-sm text-gray-700 font-medium mb-2">Report Includes:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="text-green-600">✓</span> Executive Summary
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-green-600">✓</span> Analysis & Findings
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-green-600">✓</span> Financial Calculations
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-green-600">✓</span> Professional Opinion
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-green-600">✓</span> Conclusion
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-green-600">✓</span> CPA Attestation
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                    {narrative.narrative_sections?.full_narrative?.substring(0, 200) ||
                     'Click to view and edit the complete expert report'}...
                  </p>
                  <button className="text-blue-600 text-sm font-semibold hover:underline flex items-center gap-1">
                    <Edit3 className="w-4 h-4" />
                    Review & Edit Report
                  </button>
                </div>
              </div>
            </div>

            {/* Visual Charts Section */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-indigo-600" />
                Visual Analysis Charts
                <span className="text-sm font-normal text-gray-500 ml-2">(Included in PDF export)</span>
              </h3>
              <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Claim Breakdown Chart */}
                <ClaimBreakdownChart 
                  step1={calculationResults?.step1_variable_profit_loss || 0}
                  step2={calculationResults?.step2_incremental_loss || 0}
                  total={calculationResults?.final_claim_amount || 0}
                />
                
                {/* Sensitivity Analysis Chart */}
                {calculationResults?.sensitivity_analysis && (
                  <SensitivityChart data={calculationResults.sensitivity_analysis} />
                )}
                
                {/* Monte Carlo Chart */}
                {calculationResults?.monte_carlo_analysis && (
                  <MonteCarloChart data={calculationResults.monte_carlo_analysis} />
                )}
                
                {/* No analysis message */}
                {!calculationResults?.sensitivity_analysis && !calculationResults?.monte_carlo_analysis && (
                  <div className="col-span-full bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                    <p className="text-yellow-800">
                      <strong>Statistical analyses not yet run.</strong> Go to the Report Detail page to run Sensitivity Analysis and Monte Carlo Simulation for enhanced charts.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Full Report Preview - Complete Narrative */}
            {showPreview && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <Eye className="w-6 h-6 text-blue-600" />
                  Full Report Preview
                </h3>
                <div className="space-y-8">
                  {/* Display the complete unified narrative */}
                  {narrative.narrative_sections?.full_narrative ? (
                    <div className="border-l-4 border-blue-500 pl-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-bold text-gray-900">
                          Court-Ready Expert Report
                        </h4>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSection('full_narrative');
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Edit3 className="w-4 h-4" />
                          Edit Section
                        </button>
                      </div>
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <pre className="whitespace-pre-wrap font-serif text-sm text-gray-800 leading-relaxed">
                          {narrative.narrative_sections.full_narrative}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No narrative generated yet. Click "Generate Narrative" to create your court-ready expert report.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Section editor modal */}
        {editingSection && (
          <div className="bg-white rounded-xl shadow-2xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                Editing: {sections.find(s => s.id === editingSection)?.name}
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setEditingSection(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSection}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <Save className="w-4 h-4" />
                  Save Section
                </button>
              </div>
            </div>

            {/* Text editor */}
            <textarea
              value={sectionContent}
              onChange={(e) => setSectionContent(e.target.value)}
              className="w-full h-96 p-4 border-2 border-gray-300 rounded-lg font-serif text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Edit your content here..."
            />

            {/* AI refinement */}
            <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <h3 className="font-bold mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                AI Refinement Assistant
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Tell AI how to improve this section (e.g., "Make it more formal", "Add more detail about causation", "Simplify language")
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="How should AI refine this section?"
                  className="flex-1 px-4 py-2 border rounded-lg"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.currentTarget.value) {
                      handleRefineSection(e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                    handleRefineSection(input.value);
                    input.value = '';
                  }}
                  disabled={refining}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {refining ? 'Refining...' : 'Refine'}
                </button>
              </div>
            </div>

            {/* Character count */}
            <div className="mt-4 text-right text-sm text-gray-500">
              {sectionContent.length} characters • {sectionContent.split(/\s+/).length} words
            </div>
          </div>
        )}

        {/* Help text when no narrative */}
        {!narrative && !generating && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Ready to generate your report?</h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Click "Generate AI Report" above to create a comprehensive 14-page legal narrative 
              based on your claim data and BI calculations. The AI will write all sections in 
              professional court-ready language.
            </p>
            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold mb-2">⚡ Fast</h3>
                <p className="text-sm text-gray-600">Generated in 30-60 seconds instead of 3-4 hours manual writing</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-bold mb-2">⚖️ Court-Ready</h3>
                <p className="text-sm text-gray-600">Professional legal tone, proper citations, methodology explained</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold mb-2">✏️ Editable</h3>
                <p className="text-sm text-gray-600">Review, edit, and refine each section with AI assistance</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
